import { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { NotificationService } from '../../design-system/ui-components/notification/NotificationService';
import { LocalStorageService } from 'app/core/storage/local-storage.service';
import { AuthTokenService } from 'app/core/services/auth/auth-token.service';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const localStorageService = inject(LocalStorageService);
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const authTokenService = inject(AuthTokenService);

  const appToken = authTokenService.getToken();
  const proxyToken = localStorageService.getProxyCameraToken();

  const wantsProxyToken = req.headers.get('X-Use-Proxy-Token') === 'true';

  let updatedReq = req;

  if (appToken) {
    updatedReq = updatedReq.clone({
      setHeaders: {
        Authorization: `Bearer ${appToken}`,
      },
    });
  }

  const suppliedProxyAuthorization = updatedReq.headers.get('X-Proxy-Authorization')?.trim();
  if (/^Bearer\s+(null|undefined)?$/i.test(suppliedProxyAuthorization ?? '')) {
    updatedReq = updatedReq.clone({
      headers: updatedReq.headers.delete('X-Proxy-Authorization')
    });
  }


  if (wantsProxyToken) {
    let headers = updatedReq.headers.delete('X-Use-Proxy-Token');

    if (proxyToken && proxyToken.trim() !== '') {
      headers = headers.set('X-Proxy-Authorization', `Bearer ${proxyToken}`);
    }

    updatedReq = updatedReq.clone({
      headers
    });
  }

  return next(updatedReq).pipe(
    catchError((error: HttpErrorResponse) => {

      if (isTokenError(error)) {
        handleTokenError(router, notificationService, localStorageService, authTokenService, appToken);
      }

      if (isSessionNotFoundError(error)) {
        handleTokenError(router, notificationService, localStorageService, authTokenService, appToken);
      }

      if (error.status === 503) {
        handleTokenError(router, notificationService, localStorageService, authTokenService, appToken);
      }

      return throwError(() => error);
    })
  );
};

function errorStatus(error: HttpErrorResponse): boolean {
  if (error.status === 400 || error.status === 401) {
    return true;
  } else {
    return false;
  }
}

function isTokenError(error: HttpErrorResponse): boolean {
  return error.error.meta?.errors?.some((err: any) =>
    err.parameter_name === 'token' &&
    err.type === 'invalid_parameter'
  );
}

function isSessionNotFoundError(error: any): boolean {
  const meta = error?.error?.meta;
  const errors = meta?.errors;

  return meta?.code === 401 &&
    Array.isArray(errors) &&
    errors.some(
      (e: any) =>
        e?.type === 'action_forbidden' &&
        e?.message === 'Sessão não encontrada, faça login novamente!'
    );
}

function handleTokenError(
  router: Router,
  notificationService: NotificationService,
  localStorageService: LocalStorageService,
  authTokenService: AuthTokenService,
  failedRequestToken: string | null
): void {
  if (!isFailedTokenStillCurrent(authTokenService, failedRequestToken)) {
    return;
  }

  if (!notificationService.hasNotificationOfType('error')) {
    notificationService.showError(
      'Sessão expirada.',
      'Faça login novamente para continuar',
      undefined,
      7000
    );
  }

  localStorageService.logout();
  router.navigate(['/login']);
}

function isFailedTokenStillCurrent(authTokenService: AuthTokenService, failedRequestToken: string | null): boolean {
  if (!failedRequestToken) {
    return false;
  }

  return authTokenService.getToken() === failedRequestToken;
}
